import { base64Bytes, scaledSize } from './bugReport.js';
export const SCREENSHOT_MAX_EDGE = 1600;
export const SCREENSHOT_TARGET_BYTES = 1_200_000;
function canvasFor(width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
}
/** Encode a canvas as JPEG, stepping quality down until it fits `maxBytes` (the last step is returned regardless). */
function encodeJpeg(canvas, maxBytes) {
    let dataUrl = '';
    for (const quality of [0.85, 0.75, 0.65, 0.55, 0.45]) {
        dataUrl = canvas.toDataURL('image/jpeg', quality);
        const b64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
        if (base64Bytes(b64) <= maxBytes)
            break;
    }
    return dataUrl;
}
function attachmentFromDataUrl(dataUrl, name) {
    const comma = dataUrl.indexOf(',');
    const mediaType = /^data:(image\/(?:png|jpeg));base64$/.exec(dataUrl.slice(0, comma))?.[1] ?? 'image/jpeg';
    return { kind: 'image', mediaType, data: dataUrl.slice(comma + 1), name };
}
function stamp() {
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}
/** True when this browser can capture a tab (getDisplayMedia exists; it still needs a click and a pick). */
export function canCaptureTab() {
    return typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function';
}
/**
 * Ask the browser for a tab (current tab preferred), grab one frame, stop every track, and return it as a
 * downscaled JPEG attachment. Must be called from a click. Throws with a short reason when the person cancels
 * or the browser refuses.
 */
export async function captureCurrentTab() {
    if (!canCaptureTab())
        throw new Error('this browser cannot capture a tab');
    const options = {
        video: { displaySurface: 'browser' },
        audio: false,
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
        surfaceSwitching: 'exclude',
    };
    const stream = await navigator.mediaDevices.getDisplayMedia(options);
    try {
        const video = document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.srcObject = stream;
        await video.play();
        // One frame is enough; wait for real dimensions (some browsers report 0x0 until the first frame).
        for (let i = 0; i < 20 && (!video.videoWidth || !video.videoHeight); i++)
            await new Promise((r) => setTimeout(r, 50));
        if (!video.videoWidth || !video.videoHeight)
            throw new Error('the captured tab had no picture');
        const size = scaledSize(video.videoWidth, video.videoHeight, SCREENSHOT_MAX_EDGE);
        const canvas = canvasFor(size.width, size.height);
        canvas.getContext('2d').drawImage(video, 0, 0, size.width, size.height);
        video.srcObject = null;
        return attachmentFromDataUrl(encodeJpeg(canvas, SCREENSHOT_TARGET_BYTES), `page-${stamp()}.jpg`);
    }
    finally {
        for (const track of stream.getTracks())
            track.stop();
    }
}
function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('the image could not be read'));
        img.src = src;
    });
}
/**
 * Make an attachment fit what The Lab accepts: PNG/JPEG, long edge <= 1600 px, and at most `maxBytes`. An image
 * already within all three is returned as a data URI unchanged; anything else (GIF, WebP, a 5 MB paste, a 4K
 * capture) is redrawn and re-encoded as JPEG.
 */
export async function normalizeForLab(attachment, maxBytes = SCREENSHOT_TARGET_BYTES) {
    const source = `data:${attachment.mediaType};base64,${attachment.data}`;
    const img = await loadImage(source);
    const size = scaledSize(img.naturalWidth, img.naturalHeight, SCREENSHOT_MAX_EDGE);
    const fits = (attachment.mediaType === 'image/png' || attachment.mediaType === 'image/jpeg')
        && size.width === img.naturalWidth
        && size.height === img.naturalHeight
        && base64Bytes(attachment.data) <= maxBytes;
    if (fits)
        return source;
    const canvas = canvasFor(size.width, size.height);
    const ctx = canvas.getContext('2d');
    // JPEG has no transparency: paint a white ground first so a transparent PNG does not turn black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size.width, size.height);
    ctx.drawImage(img, 0, 0, size.width, size.height);
    return encodeJpeg(canvas, maxBytes);
}
//# sourceMappingURL=screenshot.js.map